import React, { useState } from 'react';

interface Lead {
  id: number;
  name: string;
  email: string;
  phone: string;
  service: string;
  status: 'new' | 'contacted' | 'quoted' | 'won' | 'lost';
  value: string;
  date: string;
  address: string;
}

const LeadsPage: React.FC = () => {
  const [leads] = useState<Lead[]>([
    {
      id: 1,
      name: 'John Smith',
      email: 'john@email.com',
      phone: '(555) 123-4567',
      service: 'AC Installation',
      status: 'new',
      value: '$2,500',
      date: '2024-01-15',
      address: '123 Main St, Anytown'
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      email: 'sarah@company.com',
      phone: '(555) 987-6543',
      service: 'Furnace Repair',
      status: 'contacted',
      value: '$850',
      date: '2024-01-14',
      address: '456 Oak Ave, Downtown'
    },
    {
      id: 3,
      name: 'Mike Davis',
      email: 'mike@business.com',
      phone: '(555) 456-7890',
      service: 'Heat Pump Installation',
      status: 'quoted',
      value: '$4,200',
      date: '2024-01-13',
      address: '789 Pine Rd, Suburbs'
    },
    {
      id: 4,
      name: 'Lisa Wilson',
      email: 'lisa@home.com',
      phone: '(555) 321-0987',
      service: 'Duct Cleaning',
      status: 'won',
      value: '$350',
      date: '2024-01-12',
      address: '321 Elm St, Neighborhood'
    },
    {
      id: 5,
      name: 'Robert Brown',
      email: 'robert@office.com',
      phone: '(555) 654-3210',
      service: 'Commercial HVAC',
      status: 'lost',
      value: '$15,000',
      date: '2024-01-11',
      address: '654 Business Blvd, Commercial District'
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return '#17a2b8';
      case 'contacted': return '#ffc107';
      case 'quoted': return '#6f42c1';
      case 'won': return '#28a745';
      case 'lost': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const filteredLeads = filterStatus === 'all' ? leads : leads.filter(lead => lead.status === filterStatus);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1 style={{ color: '#2c3e50', marginBottom: '10px' }}>Leads Management</h1>
          <p style={{ color: '#6c757d' }}>Track and manage your sales leads</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowAddForm(true)}
        >
          + Add New Lead
        </button>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ marginRight: '10px', fontWeight: '500' }}>Filter by Status:</label>
            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="all">All Leads</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="quoted">Quoted</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </select>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <strong>Total Leads: {filteredLeads.length}</strong>
          </div>
        </div>
      </div>

      {showAddForm && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Add New Lead</h3>
          <form style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div className="form-group">
              <label>Name *</label>
              <input type="text" required />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input type="email" required />
            </div>
            <div className="form-group">
              <label>Phone *</label>
              <input type="tel" required />
            </div>
            <div className="form-group">
              <label>Service Needed *</label>
              <select required>
                <option value="">Select Service</option>
                <option value="AC Installation">AC Installation</option>
                <option value="Furnace Repair">Furnace Repair</option>
                <option value="Heat Pump Installation">Heat Pump Installation</option>
                <option value="Duct Cleaning">Duct Cleaning</option>
                <option value="Commercial HVAC">Commercial HVAC</option>
                <option value="Maintenance">Maintenance</option>
              </select>
            </div>
            <div className="form-group">
              <label>Estimated Value</label>
              <input type="text" placeholder="$0" />
            </div>
            <div className="form-group">
              <label>Address</label>
              <input type="text" />
            </div>
          </form>
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <button className="btn btn-primary">Save Lead</button>
            <button 
              className="btn btn-secondary"
              onClick={() => setShowAddForm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact</th>
              <th>Service</th>
              <th>Value</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeads.map((lead) => (
              <tr key={lead.id}>
                <td>
                  <div>
                    <strong>{lead.name}</strong>
                    <br />
                    <small style={{ color: '#6c757d' }}>{lead.address}</small>
                  </div>
                </td>
                <td>
                  <div>
                    {lead.email}
                    <br />
                    <small style={{ color: '#6c757d' }}>{lead.phone}</small>
                  </div>
                </td>
                <td>{lead.service}</td>
                <td><strong>{lead.value}</strong></td>
                <td>
                  <span 
                    className="status"
                    style={{ 
                      backgroundColor: `${getStatusColor(lead.status)}20`,
                      color: getStatusColor(lead.status)
                    }}
                  >
                    {lead.status}
                  </span>
                </td>
                <td>{lead.date}</td>
                <td>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button 
                      style={{ 
                        padding: '5px 10px', 
                        fontSize: '12px', 
                        backgroundColor: '#007bff', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                    <button 
                      style={{ 
                        padding: '5px 10px', 
                        fontSize: '12px', 
                        backgroundColor: '#28a745', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                    >
                      Convert
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeadsPage;